import { icons } from '@/components/icons';
import { checkoutUrl } from '@/api/booking';
import { escapeHtml } from '@/lib/format';
import type { Screen } from '@/router/router';

const LOAD_TIMEOUT_MS = 8000;

interface CheckoutMessage {
  type?: string;
  orderId?: string;
  url?: string;
}

export function createCheckoutScreen(): Screen {
  let rootEl!: HTMLElement;
  let bodyEl!: HTMLElement;
  let onBack!: () => void;
  let getParams!: () => Record<string, string>;
  let timer: number | undefined;
  let messageListener: ((e: MessageEvent<unknown>) => void) | undefined;

  function clearTimer(): void {
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timer = undefined;
    }
  }

  function showFallback(message: string, url: string): void {
    bodyEl.innerHTML = `
      <div class="checkout-fallback">
        <div class="state-msg">
          <h3>${escapeHtml(message)}</h3>
          <p>Apri il checkout in una nuova finestra per completare l'acquisto in sicurezza.</p>
          <a class="btn btn-pill-lime" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
            <span>Apri checkout</span>
            <span class="btn-pill-arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    `;
  }

  function render(itineraryId: string): void {
    clearTimer();
    const url = checkoutUrl(itineraryId);
    bodyEl.innerHTML = `
      <div class="checkout-loading" data-role="loading">
        <div class="spinner"></div>
        <p>Stiamo aprendo il checkout sicuro…</p>
      </div>
      <iframe
        class="checkout-frame"
        data-role="frame"
        src="${escapeHtml(url)}"
        title="Checkout"
        referrerpolicy="no-referrer-when-downgrade"
        allow="payment *"
        loading="eager"
      ></iframe>
    `;

    const frame = bodyEl.querySelector<HTMLIFrameElement>('[data-role="frame"]');
    const loadingEl = bodyEl.querySelector<HTMLElement>('[data-role="loading"]');
    if (!frame) return;

    let loaded = false;

    const onLoad = (): void => {
      loaded = true;
      clearTimer();
      if (loadingEl) loadingEl.remove();
    };
    const onError = (): void => {
      clearTimer();
      showFallback('Impossibile caricare il checkout', url);
    };

    frame.addEventListener('load', onLoad);
    frame.addEventListener('error', onError);

    // Many hosts that block iframing don't fire 'error' — they just render
    // about:blank. Fall back after a generous timeout if 'load' never fires.
    timer = window.setTimeout(() => {
      if (!loaded) {
        showFallback('Il checkout non è disponibile in iframe', url);
      }
    }, LOAD_TIMEOUT_MS);
  }

  function attachMessageListener(): void {
    if (messageListener) return;
    messageListener = (event: MessageEvent<unknown>): void => {
      try {
        const origin = new URL(event.origin);
        if (!/(^|\.)hofj\.com$/i.test(origin.hostname)) return;
      } catch {
        return;
      }
      const data = event.data as CheckoutMessage | null;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'booking:confirmed' || data.type === 'checkout:complete') {
        // Order confirmed — show a simple success state in the iframe area.
        bodyEl.innerHTML = `
          <div class="checkout-fallback">
            <div class="state-msg">
              <h3>Prenotazione confermata</h3>
              <p>Riceverai a breve la conferma via email.${
                data.orderId ? ` Riferimento: <strong>${escapeHtml(data.orderId)}</strong>.` : ''
              }</p>
              <button class="btn btn-pill-lime" data-action="home" type="button">
                <span>Torna ai viaggi</span>
                <span class="btn-pill-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        `;
        bodyEl
          .querySelector<HTMLButtonElement>('[data-action="home"]')
          ?.addEventListener('click', () => onBack());
      }
    };
    window.addEventListener('message', messageListener);
  }

  return {
    id: 'checkout',
    async mount({ el, back, params }) {
      rootEl = el;
      onBack = back;
      getParams = params;
      rootEl.id = 'wco';

      rootEl.innerHTML = `
        <header class="checkout-hd">
          <button class="checkout-back" data-action="back" type="button" aria-label="Indietro">
            ${icons.back(18, '#fff')}
          </button>
          <div class="checkout-title">
            <span class="checkout-title-main">Checkout sicuro</span>
            <span class="checkout-title-sub">booking.hofj.com</span>
          </div>
          <span class="checkout-secure" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="11" width="16" height="10" rx="2" stroke="#cff752" stroke-width="2"/>
              <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#cff752" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
        </header>
        <div class="checkout-body" data-role="body"></div>
      `;

      bodyEl = rootEl.querySelector<HTMLElement>('[data-role="body"]')!;
      rootEl
        .querySelector<HTMLButtonElement>('[data-action="back"]')
        ?.addEventListener('click', () => onBack());

      attachMessageListener();
    },
    onEnter() {
      const id = getParams().itineraryId;
      if (!id) {
        bodyEl.innerHTML = `
          <div class="checkout-fallback">
            <div class="state-msg">
              <h3>Nessun checkout attivo</h3>
              <p>Seleziona un viaggio e completa la personalizzazione per procedere al pagamento.</p>
              <button class="btn btn-pill-lime" data-action="home" type="button">
                <span>Torna ai viaggi</span>
                <span class="btn-pill-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        `;
        bodyEl
          .querySelector<HTMLButtonElement>('[data-action="home"]')
          ?.addEventListener('click', () => onBack());
        return;
      }
      render(id);
    },
    onLeave() {
      clearTimer();
    },
  };
}
